import { useMutation } from '@apollo/client';
import { ResetTabPreferencesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { ResetTabPreferencesTRPCInput } from './useResetTabPreferencesTRPC';
import { useResetTabPreferencesTRPC } from './useResetTabPreferencesTRPC';

type MutateArgs = { variables: ResetTabPreferencesTRPCInput };

/**
 * Flag-gated wrapper for the `resetTabPreferences` mutation. Defaults
 * to the legacy Apollo mutation and delegates to the tRPC variant when
 * the `trpc` feature flag is enabled.
 *
 * Returns an Apollo-compatible tuple `[mutate]` so call sites can keep
 * the `[mutate] = useMutation(...)` / `await mutate({ variables })`
 * shape (notably the `Promise.all` reset loop in `moduleContext.tsx`).
 */
export const useResetTabPreferences = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [resetTabPreferencesGraphQL] = useMutation(
    ResetTabPreferencesDocument,
    {
      update: (cache) => {
        evictField(cache, 'organisation_tab_preference');
        evictField(cache, 'user_tab_preference');
      },
    }
  );

  const trpcMutation = useResetTabPreferencesTRPC();

  const mutate = async ({ variables }: MutateArgs) => {
    if (trpcEnabled) {
      return trpcMutation.resetTabPreferences(variables);
    }

    const result = await resetTabPreferencesGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to reset tab preferences');
    }

    return result.data;
  };

  return [mutate] as const;
};
